import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap"
import ReactDOMServer from 'react-dom/server';


// textfit is an old library, and is not react compliant. However, it works, is still widely used, so
// it will do for now. There are no real viable alternatives available.
import * as textfit from "textfit";

import { IUiSchemaElemArgs } from "./SchemaController";
import { registerComponentHandler, registerJsonSchema } from "./SchemaExtensions";
import { parseVegaLikeSchema} from "./VegaLikeTools";
import { ImageEncoding, ImageOptions, StatusPanelEncoding, ActionEncoding, TextEncoding, TextOptions } from "./ExtDashboardStatusTypes";
import * as statusPanelJsonSchema from "./ExtDashboardStatusSchema.json"
import { evalString } from "./SchemaTools";

registerJsonSchema("internal:status-panel", ["*"], statusPanelJsonSchema);

declare const headerNoContentType;


type TextAlign = "left" | "right" | "center" | "auto";

interface DashboardStatusProps {
	id: string;
	text: ReactNode;
	subText: ReactNode;

	imageUrl?: string;
	imageFit?: "contain" | "cover" | "fill"; // | "none" | "scale-down";

	texts: Array<{
		text: ReactNode;
		tooltip?: ReactNode;

		showMarker?: boolean;
		markerColor?: string;
		markerBorderColor?: string;
		markerDiameter?: number;

		textColor?: string;
		textBackgroundColor?: string;
		textFontSize?: number;
		textRadius?: number;
		textPadding?: number;
		textAlign?: TextAlign;

		x: number;
		y: number;

		dx?: number;
		dy?: number;
		angle?: number;
		xOffset?: number;
		yOffset?: number;
	}>;

	actions: Array<{
		label: ReactNode;
		tooltip?: ReactNode;

		x: number;
		y: number;
		type: "button" | "link" | "switch";
		value?: number | string | boolean;

		update: (value: any) => void;
	}>;

	bindings: ReactNode;

}

function DashboardStatus(props: DashboardStatusProps) {

	const { imageUrl } = props;

	const outerRef = useRef<HTMLDivElement>();
	const innerRef = useRef<HTMLDivElement>();
	const sub1Ref = useRef<HTMLDivElement>();
	const sub2Ref = useRef<HTMLDivElement>();
	const [resizeCnt, setResizeCnt] = useState(0);


	useEffect(() => {

		const outer = outerRef.current;
		const inner = innerRef.current;

		if (!outer || !outer.clientHeight || !outer.clientWidth) { return; }

		inner.style.width = outer.clientWidth + "px";
		inner.style.height = outer.clientHeight + "px";
		// sub1Ref.current.style.backgroundColor = "#10ffff";

		sub1Ref.current.style.width  = outer.clientWidth + "px";
		sub1Ref.current.style.height = (3 * outer.clientHeight / 4) + "px";

		sub2Ref.current.style.width  = outer.clientWidth + "px";
		sub2Ref.current.style.height = outer.clientHeight / 4 + "px";
		// sub2Ref.current.style.backgroundColor = "#ff10ff";

		try {

			textfit(sub1Ref.current, {
				alignVert: true, // if true, textFit will align vertically using css tables
				alignHoriz: true, // if true, textFit will set text-align: center
				multiLine: false, // if true, textFit will not set white-space: no-wrap
				detectMultiLine: false, // disable to turn off automatic multi-line sensing
				minFontSize: 5,
				maxFontSize: 60,
				reProcess: true,  // if true, textFit will re-process already-fit nodes. Set to 'false' for better performance
				widthOnly: false, // if true, textFit will fit text to element width, regardless of text height
				alignVertWithFlexbox: false, // if true, textFit will use flexbox for vertical alignment
			});

			textfit(sub2Ref.current, {
				alignVert: false, alignHoriz: true,
				multiLine: false, detectMultiLine: false,
				minFontSize: 2,
				maxFontSize: 15,
				reProcess: true, widthOnly: false,	alignVertWithFlexbox: false,
			});

		} catch (e) {
			console.log(e);
		}

	}, [resizeCnt, props.text, props.subText, outerRef.current?.clientWidth, outerRef.current?.clientHeight]);


	useEffect(() => {

		const resizeObserver = new ResizeObserver((entries) => setResizeCnt(prev => prev + 1));
		resizeObserver.observe(outerRef.current);
		return () => resizeObserver.disconnect();

	}, []);






	// Since the text in the target div will be parsed by the textfit library which is NOT a react library, 
	// we need to create the divs in a way that the sub-elements are not tracked by react. This is because
	// the textfit library may remove and add new children under the div. So we cannot create the text
	// fields in the normal way. Instead we render it to html and insert it as inner html.

	return <div style={{ position: "relative", width: "100%", height: "100%" }} ref={outerRef}>

			{imageUrl && <img src={imageUrl} style={{ position: "absolute", width: "100%", height: "100%", objectFit: props.imageFit }} alt=""/>}
			{(props.texts || []).map((textElem, i) => {
				const { text, x, y, textAlign, showMarker } = textElem;

				const align = !textAlign || textAlign === "auto" 
					? showMarker ? ((x < 50 ) ? "left" : "right") : "center"
					: textAlign;

				const markerDiameter = textElem.markerDiameter || 12;
				const markerOffset = showMarker ? markerDiameter / 2 + 3 : 0;

				const textTransform: string[] = [];
				const translate = align === "left" ? `translate(0px, -50%)` : 
										align === "right" ? `translate(0px, -50%)` :
											align === "center" ? `translate(-50%, -50%)` : undefined;

				const dx = align === "left" ? markerOffset : align === "right" ? -markerOffset : 0;

				if (textElem.xOffset || textElem.yOffset) {
					textTransform.push(`translate(${textElem.xOffset || 0}px, ${textElem.yOffset || 0}px)`);
				}
				if (textElem.angle != null) {
					textTransform.push(`rotate(${textElem.angle}deg)`);
				}
				if (translate) {
					textTransform.push(translate);
				}
				if (textElem.dx || textElem.dy || dx) {
					textTransform.push(`translate(${(textElem.dx || 0) + (dx || 0)}px, ${textElem.dy || 0}px)`);
				}

				return (
				<React.Fragment key={"texts_" + i}>
					{showMarker && <div
						style={{
							position: "absolute",
							left: x + "%",
							top: y + "%",
							width: markerDiameter,
							height: markerDiameter,
							background: textElem.markerColor || "blue",
							borderRadius: "50%",
							border: "1px solid " + (textElem.markerBorderColor || "black"),
							transform: "translate(-50%, -50%)",
							pointerEvents: "none",
							boxShadow: "0 0 4px #0008",
//							zIndex: 2,
						}}
					/>}
					<div
						style={{
							position: "absolute",
							left: align !== "right" ? x + "%" : undefined ,
							right: align === "right" ? (100 - x) + "%" : undefined,
							transform: textTransform.length > 0 ? textTransform.join(" ") : undefined,
							transformOrigin: `0 0`,

							top: y + "%",
							color: textElem.textColor || "black",
							backgroundColor: textElem.textBackgroundColor || "#ffffff00",
							borderRadius: textElem.textRadius || undefined,
							padding: typeof textElem.textPadding === "number" ? textElem.textPadding + "px" : "1px",
							fontSize: textElem.textFontSize || 12,
							
//							zIndex: 3,
						}}
					>
						{text}
					</div>
				</React.Fragment>
				);
			})}

			{(props.actions || []).map((actionElem, i) => {
				const { label, x, y, type } = actionElem;

				if (type === "button") {
					return (
						<Button 
							size="sm" variant="secondary"
							className="gs-no-drag"
							key={"action_" + i}
							style={{
								position: "absolute",
								left: x + "%",
								top: y + "%",
								zIndex: 1,
								transform: "translate(-50%, -50%)",
							}}
							onClick={(e) => {
								e.stopPropagation();
								actionElem.update(actionElem.value);
							}}
						>
							{label}
						</Button>
					);

				} else if (type === "switch") {

					return (
						<Form.Check 
							className="gs-no-drag"
							type="switch"
							key={"action_" + i}
							style={{
								position: "absolute",
								left: x + "%",
								top: y + "%",
								zIndex: 1,
								transform: "translate(-50%, -50%)",
							}}
							onChange={(e) => actionElem.update(e.target.checked)}
							label={label}
						/>

					);
				}
				return null;
			})}

			<div style={{ position: "absolute" }} ref={innerRef} >
				<div ref={sub1Ref} dangerouslySetInnerHTML={{__html: ReactDOMServer.renderToString(props.text)}}></div>
				<div ref={sub2Ref} dangerouslySetInnerHTML={{__html: ReactDOMServer.renderToString(props.subText)}}></div>
			</div>

			{props.bindings}

		</div>;

}






type XY = { x: number; y: number };

export const DashboardStatusExtension = (props: {args: IUiSchemaElemArgs}) => {

	const { args } = props;
	const { value, title } = args;
	const memRef = useRef<any>({});
	const [needUpdate, setNeedUpdate] = useState(0);

	let text: string | ReactNode = "";
	let subText: string | ReactNode = "";
	let imageUrl: string = "";
	let imageFit: "contain" | "cover" | "fill" = "contain";
	let texts: DashboardStatusProps["texts"] = [];
	let actions: DashboardStatusProps["actions"] = [];

	let projectMercatorToImage: ((lngLat: [number, number]) => { x: number; y: number }) | null = null;
	let bindings: ReactNode = null;

	try {
		if (value != null) {
			if (["string", "number", "boolean"].includes(typeof value)) {

				text = String(value);
				subText = title;

			} else if (typeof value === "object" && value.$schema) {

				bindings = parseVegaLikeSchema({ mark: "status-panel", ...value }, ["image", "status-panel", "text", "action"],
							memRef.current, () => setNeedUpdate(needUpdate + 1),
							(layer, readValue, readValueFn) => {
					
					if (layer.mark === "image") {
						// the IMAGE layer is used to display an image on the card. I loads the image from a URL.
						// The image is typically used as a background image, and sensors and sensor values can be
						// displayed on top of the image. To auto locate the sensors according to their lat/lng
						// coordinates, the image layer must define two reference points. The reference points are
						// used to project the lat/lng coordinates to the image coordinates. Alternatively, the
						// the sensors can be placed directly via x,y (in percentage) coordinates.

						const encoding: ImageEncoding = layer.encoding;
						const options: ImageOptions  = layer.options;

						imageUrl = readValue<string>(encoding.imageUrl, "string", 0);
						imageFit = options?.imageFit || "contain";

						if (encoding.referencePoints) {
							try {
								const R = 6378137; // Earth radius in meters
								const { point1, point2 } = encoding.referencePoints;

								// Convert lat/lng to local projected XY using equirectangular approximation
								const lat0 = point1.lat * Math.PI / 180;
								const lng0 = point1.lng * Math.PI / 180;
							
								if (encoding.referencePoints.show) {
									for (const point of [point1, point2]) {
										texts.push({ 
											text: args.stringToComponent("lat:" + point.lat.toFixed(6) + ",lng:" + point.lng.toFixed(6)),
											x: point.x, y: point.y,
											showMarker: true,
											markerColor: "green",
											markerBorderColor: "black",
										});
									}
								}

								const toLocalMeters = (lat: number, lng: number): XY => {
									const phi = lat * Math.PI / 180;
									const lambda = lng * Math.PI / 180;
									const y = R * (lambda - lng0) * Math.cos(lat0);
									const x = R * (phi - lat0);
									return { x, y };
								}
							
								// World (meter) coords for reference points
								const world1 = toLocalMeters(point1.lat, point1.lng);
								const world2 = toLocalMeters(point2.lat, point2.lng);
							
								// Vectors
								const dx_world = world2.x - world1.x;
								const dy_world = world2.y - world1.y;
								const dx_screen = point2.x - point1.x;
								const dy_screen = point2.y - point1.y;
							
								// Scale
								const scale = Math.sqrt(dx_screen**2 + dy_screen**2) / Math.sqrt(dx_world**2 + dy_world**2);
							
								// Rotation angle between world and screen vectors
								const angle_world = Math.atan2(dy_world, dx_world);
								const angle_screen = Math.atan2(dy_screen, dx_screen);
								const rotation = angle_screen - angle_world;
							
								// Projector function
								projectMercatorToImage = (lnglat: number[]) => {
									const p = toLocalMeters(lnglat[1], lnglat[0]);
									const dx = p.x - world1.x;
									const dy = p.y - world1.y;
							
									// Rotate
									const dx_rot = dx * Math.cos(rotation) - dy * Math.sin(rotation);
									const dy_rot = dx * Math.sin(rotation) + dy * Math.cos(rotation);
							
									// Scale and translate
									return {
										x: point1.x + scale * dx_rot,
										y: point1.y + scale * dy_rot,
										dx, dy
									};
								};
							} catch (e) {
								console.error("Error setting up projection:", e);
								projectMercatorToImage = null;
							}

						}


					} else if (layer.mark === "status-panel") {

						// the status-panel layer is used to display a status panel on the card. It has only two fields:
						//  - value: the value to display
						//  - title: the title to display
						// The value is typically a number, and the title is a string. The value can be a number or a string, and the font used
						// for the value will automatically adjust to fit the card. 

						const encoding: StatusPanelEncoding = layer.encoding;

						text = readValue<string | number>(encoding.value, "string", 0);		// FIXME: could be string or number
						text = text != null ? args.stringToComponent(String(text)) : null;

						subText = readValue<string>(encoding.title, "string", 0);
						subText = subText != null ? args.stringToComponent(String(subText)) : null;

						
					} else if (layer.mark === "text") {

						const encoding: TextEncoding = layer.encoding;
						const options: TextOptions  = layer.options;
						const textFn = readValueFn(encoding.text, "string");
						const tooltipFn = readValueFn(encoding.tooltip, "string");

						let cnv: (val: any) => { x: number, y: number } = null;

						if (encoding.x != null && encoding.y != null) {
							const xFn = readValue(encoding.x, "number") as any;
							const yFn = readValue(encoding.y, "number") as any;
							cnv = (val: any) => {
								return {
									x: (typeof xFn === "function" ? xFn(val) : xFn) || 0,
									y: (typeof yFn === "function" ? yFn(val) : yFn) || 0
								};
							}
						} else if (encoding.latitude != null && encoding.longitude != null) {
							const latFn = readValue(encoding.latitude, "number") as any;
							const lngFn = readValue(encoding.longitude, "number") as any;
							cnv = (val: any) => {
								const lat = (typeof latFn === "function" ? latFn(val) : latFn) || 0;
								const lng = (typeof lngFn === "function" ? lngFn(val) : lngFn) || 0;
								if (lat == null || lng == null || !projectMercatorToImage) return { x: 0, y: 0 };
								const { x, y } = projectMercatorToImage([lng, lat]);
								return { x, y };
							}
						}

						const showMarkerFn = encoding.showMarker != null ? readValueFn<boolean>(encoding.showMarker, "boolean") : false;
						const markerColorFn = encoding.markerColor != null ? readValueFn<string>(encoding.markerColor, "string") : "blue";
						const markerBorderColorFn = encoding.markerColor != null ? readValueFn<string>(encoding.markerBorderColor, "string") : "black";

						const textAlignFn = encoding.textAlign != null ? readValueFn<TextAlign>(encoding.textAlign, "string") : "auto";
						const textColorFn = encoding.textColor != null ? readValueFn<string>(encoding.textColor, "string") : "black";
						const textBackgroundColorFn = encoding.textBackgroundColor != null ? readValueFn<string>(encoding.textBackgroundColor, "string") : "#ffffff00";
						const textRadiusFn = encoding.textRadius != null ? readValueFn<number>(encoding.textRadius, "number") : 0;
						const textPaddingFn = encoding.textPadding != null ? readValueFn<number>(encoding.textPadding, "number") : 0;
						const textFontSizeFn = encoding.textFontSize != null ? readValueFn<number>(encoding.textFontSize, "number") : 12;

						const dx      = options?.dx != null ? readValue<number>(options.dx, "number", 0) : 0;
						const dy      = options?.dy != null ? readValue<number>(options.dy, "number", 0) : 0;
						const angle   = options?.angle != null ? readValue<number>(options.angle, "number", 0) : 0;
						const xOffset = options?.xOffset != null ? readValue<number>(options.xOffset, "number", 0) : 0;
						const yOffset = options?.yOffset != null ? readValue<number>(options.yOffset, "number", 0) : 0;



						for (const v of layer.data?.values || []) {
							const {x, y} = cnv(v);
							const text = typeof textFn === "function" ? textFn(v) : textFn;
							const tooltip = (typeof tooltipFn === "function" ? tooltipFn(v) : tooltipFn);

							if (text != null) {
								texts.push({ 
//									text: args.stringToComponent(String(text + `${x} ${y}`)),
									text: args.stringToComponent(String(text)),
									tooltip: tooltip ? args.stringToComponent(String(tooltip)) : undefined,
									x, y, dx, dy, angle, xOffset, yOffset,
									showMarker: typeof showMarkerFn === "function" ? showMarkerFn(v) : showMarkerFn,
									markerColor: typeof markerColorFn === "function" ? markerColorFn(v) : markerColorFn,
									markerBorderColor: typeof markerBorderColorFn === "function" ? markerBorderColorFn(v) : markerBorderColorFn,
									textAlign: typeof textAlignFn === "function" ? textAlignFn(v) : textAlignFn,
									textColor: typeof textColorFn === "function" ? textColorFn(v) : textColorFn,
									textBackgroundColor: typeof textBackgroundColorFn === "function" ? textBackgroundColorFn(v) : textBackgroundColorFn,
									textRadius: typeof textRadiusFn === "function" ? textRadiusFn(v) : textRadiusFn,
									textPadding: typeof textPaddingFn === "function" ? textPaddingFn(v) : textPaddingFn,
									textFontSize: typeof textFontSizeFn === "function" ? textFontSizeFn(v) : textFontSizeFn,
								});
							}
						}

					} else if (layer.mark === "action") {

						// The action layer is used to add buttons to the card. Buttons can be one of:
						//  - button: a normal button
						//  - link: a link to a URL
						//  - switch: a toggle switch
						// The action layer is used to add buttons to the card. Buttons can be one of:

						const encoding: ActionEncoding = layer.encoding;
						const labelFn   = readValueFn<string>(encoding.label, "string");
						const tooltipFn = readValueFn(encoding.tooltip, "string");
						const typeFn    = readValueFn(encoding.type, "string");
						let cnv: (val: any) => { x: number, y: number } = null;

						if (encoding.x != null && encoding.y != null) {
							const xFn = readValueFn(encoding.x, "number");
							const yFn = readValueFn(encoding.y, "number");
							cnv = (val: any) => {
								return {
									x: (typeof xFn === "function" ? xFn(val) : xFn) || 0,
									y: (typeof yFn === "function" ? yFn(val) : yFn) || 0
								};
							}
						} else if (encoding.latitude != null && encoding.longitude != null) {
							const latFn = readValueFn(encoding.latitude, "number");
							const lngFn = readValueFn(encoding.longitude, "number");
							cnv = (val: any) => {
								const lat = (typeof latFn === "function" ? latFn(val) : latFn) || 0;
								const lng = (typeof lngFn === "function" ? lngFn(val) : lngFn) || 0;
								if (lat == null || lng == null || !projectMercatorToImage) return { x: 0, y: 0 };
								const { x, y } = projectMercatorToImage([lng, lat]);
								return { x, y };
							}
						}

						const actionTypeFn    = readValueFn<string>(encoding.actionType,    "string");
						const actionUrlFn     = readValueFn<string>(encoding.actionUrl,     "string");
						const actionMethodFn  = readValueFn<string>(encoding.actionMethod,  "string");
						const actionHeadersFn = readValueFn<string>(encoding.actionHeaders, "string");
						const actionBodyFn    = readValueFn<string>(encoding.actionBody,    "string");


						for (const v of layer.data?.values || []) {
							const {x, y} = cnv(v);
							const label   = (typeof labelFn === "function"   ? labelFn(v)   : labelFn);
							const tooltip = (typeof tooltipFn === "function" ? tooltipFn(v) : tooltipFn);
							const type    = (typeof typeFn === "function"    ? typeFn(v)    : typeFn);

							if (type != null) {
								actions.push({ 
									label: label ? args.stringToComponent(String(label)) : undefined,
									tooltip: tooltip ? args.stringToComponent(String(tooltip)) : undefined,
									x, y,
									type: type === "button" || type === "link" || type === "switch" ? type : "button",
									update: (value: any) => {
										const actionType = (typeof actionTypeFn === "function" ? actionTypeFn(v) : actionTypeFn);
										if (actionType === "url") {

											const actionUrl     = (typeof actionUrlFn === "function"     ? actionUrlFn(v)     : actionUrlFn);
											const actionMethod  = (typeof actionMethodFn === "function"  ? actionMethodFn(v)  : actionMethodFn);
											const actionHeaders = (typeof actionHeadersFn === "function" ? actionHeadersFn(v) : actionHeadersFn);
											const actionBody    = (typeof actionBodyFn === "function"    ? actionBodyFn(v)    : actionBodyFn);

											if (actionUrl) {

												const url  = String(evalString(args.uiElem.trust, actionUrl, args.lib, args.objects, { readOnly: args.readOnly, value }));
												const body = actionBody ? evalString(args.uiElem.trust, actionBody, args.lib, args.objects,
																						{ readOnly: args.readOnly, value }) : null;
												
												(async () => {
													try {
														const headers = actionHeaders ? JSON.parse(actionHeaders) : {};
														const response = await fetch(url, {
															method: actionMethod || "GET",
															headers: {
																"Content-Type": "application/json",
																...headers,
																...headerNoContentType
															},
															body: body != null ? (typeof body === "string" ? body : JSON.stringify(body)) : undefined,
														});
														if (!response.ok) {
															console.error("Error calling action", response.status, response.statusText);
														} else {
															console.log("Action called", response.status, response.statusText);
														}
													} catch (e) {
														console.error("Error calling action", e);
													}
												})();
											}
										}
										console.log("Action update", type, value);

									}
								});
							}
						}
					}

				});

			}
		}
	} catch (e) {
		text = "";
		subText = "";
	}

	return <DashboardStatus 
		key={args.fullkey} id={args.fullkey}
		text={text} subText={subText} 
		imageUrl={imageUrl} imageFit={imageFit}
		texts={texts}
		actions={actions}
		bindings={bindings}
	/>
};

registerComponentHandler("dashboard-status", (args) => <DashboardStatusExtension key={args.fullkey} args={args} />);
